import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { projects } from '../../data/projects';

const ProjectDetail = () => {
  const { projectId } = useParams();
  const project = projects.find(p => p.id === projectId);

  if (!project) {
    return (
      <div className="page-content">
        <section className="section">
          <div className="container" style={{textAlign: 'center', padding: '4rem 0'}}>
            <h1 style={{color: 'var(--accent-blue)', marginBottom: '1rem'}}>Project Not Found</h1>
            <p style={{marginBottom: '2rem'}}>The project you're looking for doesn't exist.</p>
            <Link to="/projects" style={{color: 'var(--accent2-red)', fontWeight: 'bold'}}>
              ← Back to Projects
            </Link>
          </div>
        </section>
      </div>
    );
  }

  // Get related projects (same category, exclude current)
  const relatedProjects = projects
    .filter(p => p.category === project.category && p.id !== project.id)
    .slice(0, 3);

  return (
    <div className="page-content">
      {/* Hero Section with Project Image */}
      <section className="page-hero" style={{
        position: 'relative',
        backgroundImage: `url("${project.featuredImage}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        overflow: 'hidden',
        minHeight: '400px'
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0.7) 100%)',
          zIndex: 1
        }}></div>
        
        <div className="container" style={{position: 'relative', zIndex: 2}}>
          <div style={{marginBottom: '1rem'}}>
            <Link 
              to="/projects" 
              style={{
                color: 'var(--white)',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '1rem'
              }}
            >
              <span className="material-icons">arrow_back</span>
              Back to Projects
            </Link>
          </div>
          <div style={{
            display: 'inline-block',
            background: 'var(--accent2-red)',
            color: 'var(--white)',
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            fontSize: '0.9rem',
            fontWeight: 'bold',
            marginBottom: '1rem'
          }}>
            {project.category}
          </div>
          <h1 style={{color: 'var(--white)', textShadow: '2px 2px 4px rgba(0, 0, 0, 0.5)', marginBottom: '0.5rem'}}>
            {project.title}
          </h1>
          <p style={{color: 'rgba(255, 255, 255, 0.95)', fontSize: '1.2rem', textShadow: '1px 1px 3px rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', gap: '1rem'}}>
            <span style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
              <span className="material-icons">location_on</span>
              {project.location}
            </span>
            <span>•</span>
            <span>{project.year}</span>
          </p>
        </div>
      </section>

      {/* Project Overview */}
      <section className="section">
        <div className="container">
          <div className="precon-grid-2col" style={{display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '3rem', alignItems: 'start'}}>
            <div>
              <h2 style={{color: 'var(--accent-blue)', marginBottom: '1rem'}}>Project Overview</h2>
              <p style={{fontSize: '1.1rem', lineHeight: '1.8', color: 'var(--gray-600)', marginBottom: '2rem'}}>
                {project.description}
              </p>

              {project.scope && (
                <>
                  <h3 style={{color: 'var(--accent-blue)', marginBottom: '1rem'}}>Project Scope</h3>
                  <p style={{fontSize: '1rem', lineHeight: '1.7', color: 'var(--gray-600)', marginBottom: '2rem'}}>
                    {project.scope}
                  </p>
                </>
              )}
            </div>

            <div style={{background: 'var(--secondary-light)', padding: '2rem', borderRadius: '10px'}}>
              <h3 style={{color: 'var(--accent-blue)', marginBottom: '1.5rem', fontSize: '1.3rem'}}>Project Details</h3>
              
              <div style={{marginBottom: '1.5rem'}}>
                <div style={{fontSize: '0.9rem', color: 'var(--gray-600)', marginBottom: '0.25rem'}}>Client</div>
                <div style={{fontWeight: 'bold', color: 'var(--accent-blue)'}}>{project.client}</div>
              </div>

              <div style={{marginBottom: '1.5rem'}}>
                <div style={{fontSize: '0.9rem', color: 'var(--gray-600)', marginBottom: '0.25rem'}}>Location</div>
                <div style={{fontWeight: 'bold', color: 'var(--accent-blue)'}}>{project.location}</div>
              </div>

              <div style={{marginBottom: '1.5rem'}}>
                <div style={{fontSize: '0.9rem', color: 'var(--gray-600)', marginBottom: '0.25rem'}}>Year Completed</div>
                <div style={{fontWeight: 'bold', color: 'var(--accent-blue)'}}>{project.year}</div>
              </div>

              {project.generalContractor && (
                <div style={{marginBottom: '1.5rem'}}>
                  <div style={{fontSize: '0.9rem', color: 'var(--gray-600)', marginBottom: '0.25rem'}}>General Contractor</div>
                  <div style={{fontWeight: 'bold', color: 'var(--accent-blue)'}}>{project.generalContractor}</div>
                </div>
              )}

              {project.architect && (
                <div style={{marginBottom: '1.5rem'}}>
                  <div style={{fontSize: '0.9rem', color: 'var(--gray-600)', marginBottom: '0.25rem'}}>Architect</div>
                  <div style={{fontWeight: 'bold', color: 'var(--accent-blue)'}}>{project.architect}</div>
                </div>
              )}

              {project.engineer && (
                <div style={{marginBottom: '1.5rem'}}>
                  <div style={{fontSize: '0.9rem', color: 'var(--gray-600)', marginBottom: '0.25rem'}}>Engineer</div>
                  <div style={{fontWeight: 'bold', color: 'var(--accent-blue)'}}>{project.engineer}</div>
                </div>
              )}

              <div>
                <div style={{fontSize: '0.9rem', color: 'var(--gray-600)', marginBottom: '0.5rem'}}>Services Provided</div>
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '0.5rem'}}>
                  {project.services.map((service, index) => (
                    <span key={index} style={{
                      background: 'var(--accent-blue)',
                      color: 'var(--white)',
                      padding: '0.4rem 0.8rem',
                      borderRadius: '15px',
                      fontSize: '0.85rem'
                    }}>
                      {service}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features & Challenges */}
      <section className="section" style={{background: 'var(--secondary-light)'}}>
        <div className="container">
          <div className="precon-grid-2col" style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '3rem'}}>
            {project.features && (
              <div>
                <h3 style={{color: 'var(--accent-blue)', marginBottom: '1.5rem', fontSize: '1.5rem'}}>Key Features</h3>
                <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                  {project.features.map((feature, index) => (
                    <li key={index} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      marginBottom: '1rem',
                      fontSize: '1rem',
                      color: 'var(--gray-600)',
                      lineHeight: '1.6'
                    }}>
                      <span className="material-icons" style={{color: 'var(--accent-blue)', marginTop: '0.1rem'}}>check_circle</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {project.challenges && (
              <div>
                <h3 style={{color: 'var(--accent2-red)', marginBottom: '1.5rem', fontSize: '1.5rem'}}>Project Challenges</h3>
                <ul style={{listStyle: 'none', padding: 0, margin: 0}}>
                  {project.challenges.map((challenge, index) => (
                    <li key={index} style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '1rem',
                      marginBottom: '1rem',
                      fontSize: '1rem',
                      color: 'var(--gray-600)',
                      lineHeight: '1.6'
                    }}>
                      <span className="material-icons" style={{color: 'var(--accent2-red)', marginTop: '0.1rem'}}>engineering</span>
                      <span>{challenge}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Project Links & Resources */}
      {project.links && project.links.length > 0 && (
        <section className="section" style={{background: 'var(--secondary-light)'}}>
          <div className="container">
            <h2 style={{color: 'var(--accent-blue)', marginBottom: '2rem'}}>Project Links & Resources</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              gap: '1.5rem'
            }}>
              {project.links.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1rem',
                    padding: '1.5rem',
                    background: 'var(--white)',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    color: 'var(--accent-blue)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    border: '2px solid transparent'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.15)';
                    e.currentTarget.style.borderColor = 'var(--accent2-red)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.1)';
                    e.currentTarget.style.borderColor = 'transparent';
                  }}
                >
                  <span className="material-icons" style={{
                    color: 'var(--accent2-red)',
                    fontSize: '2rem'
                  }}>
                    link
                  </span>
                  <div style={{flex: 1}}>
                    <div style={{
                      fontWeight: 'bold',
                      fontSize: '1rem',
                      marginBottom: '0.25rem'
                    }}>
                      {link.title}
                    </div>
                    <div style={{
                      fontSize: '0.85rem',
                      color: 'var(--gray-600)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}>
                      View Resource
                      <span className="material-icons" style={{fontSize: '1rem'}}>
                        open_in_new
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Project Gallery */}
      <section className="section">
        <div className="container">
          <h2 style={{color: 'var(--accent-blue)', marginBottom: '2rem', textAlign: 'center'}}>Project Gallery</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
            gap: '1.5rem'
          }}>
            {project.images.map((image, index) => (
              <div key={index} style={{
                paddingBottom: '75%',
                position: 'relative',
                background: 'var(--secondary-light)',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
              }}>
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '3rem',
                  color: 'var(--accent-blue)'
                }}>
                  <span className="material-icons" style={{fontSize: 'inherit'}}>image</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Projects */}
      {relatedProjects.length > 0 && (
        <section className="section" style={{background: 'var(--secondary-light)'}}>
          <div className="container">
            <h2 style={{color: 'var(--accent-blue)', marginBottom: '2rem', textAlign: 'center'}}>Related Projects</h2>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '2rem'
            }}>
              {relatedProjects.map((relatedProject) => (
                <Link 
                  key={relatedProject.id}
                  to={`/projects/${relatedProject.id}`}
                  style={{textDecoration: 'none', color: 'inherit'}}
                >
                  <div style={{
                    background: 'var(--white)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)',
                    transition: 'transform 0.3s ease'
                  }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-8px)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  >
                    <div style={{
                      paddingBottom: '66.67%',
                      position: 'relative',
                      background: 'var(--secondary-light)'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '3rem',
                        color: 'var(--accent-blue)'
                      }}>
                        <span className="material-icons" style={{fontSize: 'inherit'}}>image</span>
                      </div>
                    </div>
                    <div style={{padding: '1.5rem'}}>
                      <h3 style={{fontSize: '1.2rem', color: 'var(--accent-blue)', marginBottom: '0.5rem'}}>{relatedProject.title}</h3>
                      <p style={{fontSize: '0.9rem', color: 'var(--gray-600)'}}>{relatedProject.location} • {relatedProject.year}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProjectDetail;

